import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { BookOpen, Clock, Search, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

const IssuedBooks = () => {
  const [issuedBooks, setIssuedBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Issued');
  const [returnModal, setReturnModal] = useState({ open: false, issue: null });
  const [fineAmount, setFineAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  useEffect(() => {
    fetchIssuedBooks();
  }, []);

  useEffect(() => {
    filterBooks();
  }, [issuedBooks, searchTerm, statusFilter]);

  const fetchIssuedBooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/issues`, { headers: getAuthHeaders() });
      setIssuedBooks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching issued books:', error);
      setIssuedBooks([]);
    }
  };

  const filterBooks = () => {
    let filtered = issuedBooks;

    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(book => book.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(book =>
        book.book_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.member_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.member_card_number.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredBooks(filtered);
  };

  // Pagination calculations
  const totalRecords = filteredBooks.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalRecords);
  const paginatedBooks = filteredBooks.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handleReturnBook = async () => {
    if (!returnModal.issue) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/library/issues/${returnModal.issue.id}/return?returned_to=Current User&fine_amount=₹{fineAmount}`,
        { method: 'PUT' }
      );

      if (response.ok) {
        alert('Book returned successfully!');
        setReturnModal({ open: false, issue: null });
        setFineAmount(0);
        fetchIssuedBooks();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to return book');
      }
    } catch (error) {
      console.error('Error returning book:', error);
      alert('Failed to return book');
    } finally {
      setLoading(false);
    }
  };

  const openReturnModal = (issue) => {
    const today = new Date();
    const dueDate = new Date(issue.due_date);
    const overdueDays = Math.max(0, Math.ceil((today - dueDate) / (1000 * 60 * 60 * 24)));
    const calculatedFine = overdueDays * 2; // ₹2 per day fine

    setFineAmount(calculatedFine);
    setReturnModal({ open: true, issue });
  };

  const getStatusBadge = (issue) => {
    const today = new Date();
    const dueDate = new Date(issue.due_date);
    const isOverdue = issue.status === 'Issued' && today > dueDate;

    if (issue.status === 'Returned') {
      return <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">Returned</span>;
    } else if (isOverdue) {
      return <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">Overdue</span>;
    } else {
      return <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">Issued</span>;
    }
  };

  // Bug fix: removed shadowing formatDate – using imported one

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Issued Books Management
            </CardTitle>
            <Button onClick={fetchIssuedBooks} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by book title, member name, or card number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Books</SelectItem>
                <SelectItem value="Issued">Currently Issued</SelectItem>
                <SelectItem value="Returned">Returned</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="text-sm text-gray-600">
            Showing {startIndex + 1} to {endIndex} of {totalRecords} records (Total: {issuedBooks.length})
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Book Title</th>
                  <th className="text-left p-3">Member</th>
                  <th className="text-left p-3">Issue Date</th>
                  <th className="text-left p-3">Due Date</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Fine</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBooks.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center p-4 text-gray-500">
                      No issued books found
                    </td>
                  </tr>
                ) : (
                  paginatedBooks.map((issue) => {
                    const today = new Date();
                    const dueDate = new Date(issue.due_date);
                    const isOverdue = issue.status === 'Issued' && today > dueDate;

                    return (
                      <tr key={issue.id} className={`border-b hover:bg-gray-50 ${isOverdue ? 'bg-red-50' : ''}`}>
                        <td className="p-3">
                          <div className="font-medium">{issue.book_title}</div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium">{issue.member_name}</div>
                          <div className="text-sm text-gray-600">{issue.member_card_number}</div>
                        </td>
                        <td className="p-3">{formatDate(issue.issue_date)}</td>
                        <td className="p-3">
                          <div className={isOverdue ? 'text-red-600 font-medium' : ''}>
                            {formatDate(issue.due_date)}
                            {isOverdue && (
                              <div className="text-xs text-red-500">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {Math.ceil((today - dueDate) / (1000 * 60 * 60 * 24))} days overdue
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-3">{getStatusBadge(issue)}</td>
                        <td className="p-3">
                          {issue.fine_amount > 0 ? (
                            <span className="text-red-600 font-medium">₹{issue.fine_amount}</span>
                          ) : (
                            <span className="text-gray-400">₹0</span>
                          )}
                        </td>
                        <td className="p-3">
                          {issue.status === 'Issued' && (
                            <Button
                              size="sm"
                              onClick={() => openReturnModal(issue)}
                              variant="outline"
                            >
                              Return
                            </Button>
                          )}
                          {issue.status === 'Returned' && (
                            <span className="text-gray-400 text-sm">
                              Returned on {formatDate(issue.return_date)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalRecords > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
              
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="px-3 py-1 text-sm">Page {currentPage} of {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={returnModal.open} onOpenChange={(open) => setReturnModal({ open, issue: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return Book</DialogTitle>
          </DialogHeader>
          {returnModal.issue && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <p><strong>Book:</strong> {returnModal.issue.book_title}</p>
                <p><strong>Member:</strong> {returnModal.issue.member_name}</p>
                <p><strong>Issue Date:</strong> {formatDate(returnModal.issue.issue_date)}</p>
                <p><strong>Due Date:</strong> {formatDate(returnModal.issue.due_date)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Fine Amount ($)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={fineAmount}
                  onChange={(e) => setFineAmount(parseFloat(e.target.value) || 0)}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Calculated fine based on overdue days
                </p>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setReturnModal({ open: false, issue: null })}
                >
                  Cancel
                </Button>
                <Button onClick={handleReturnBook} disabled={loading}>
                  {loading ? 'Processing...' : 'Confirm Return'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IssuedBooks;